// src/components/biometrics/ModalDetailBiometrics.tsx
import {
	CalendarToday,
	CameraAltOutlined,
	Close,
	ContentCopy as ContentCopyIcon,
	Download as DownloadIcon,
	Link as LinkIcon,
	Person2Outlined,
} from "@mui/icons-material";
import {
	Avatar,
	Box,
	Card,
	CardContent,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	Link,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { useMemo } from "react";
import { IntegratorChip, StatusChip } from "../BioStatusChips";
import { useBiometricsData } from "../../hooks/useBiometricsData";
import { formatDateTime } from "../../hooks/mappers";
import InfoItemWithCopy from "../../../../shared/components/InfoItemWithCopy/InfoItemWithCopy";
import { maskPhone } from "../../../../shared/utils";

export function ModalDetailBiometrics({ isOpen, onClose, biometricsId }: { isOpen: boolean; onClose: () => void; biometricsId: string }) {
	const { data: ui, loading, photoError, setPhotoError } = useBiometricsData(isOpen, biometricsId);

	const canDownload = !!ui?.photoUrl && !photoError;

	const headerRight = useMemo(
		() => (
			<Stack direction="row" spacing={1} alignItems="center">
				<StatusChip status={ui?.status} />
				<IntegratorChip status={ui?.integratorStatus ?? undefined} />
			</Stack>
		),
		[ui?.status, ui?.integratorStatus]
	);

	const handleDownload = () => {
		if (!ui?.photoUrl) return;
		const a = document.createElement("a");
		a.href = ui.photoUrl;
		a.download = `biometria_${ui?.id ?? "foto"}.png`;
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 3,
					overflow: "hidden",
					boxShadow: (t: any) => t.shadows[8],
					backdropFilter: "blur(4px)",
				},
			}}
		>
			<DialogTitle sx={{ py: 1.5, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
				<Stack spacing={0}>
					<Typography variant="subtitle2" color="text.secondary" lineHeight={1}>
						Validação biométrica
					</Typography>
					<Typography variant="h6" fontWeight={700}>
						Detalhes
					</Typography>
				</Stack>
				<Stack direction="row" spacing={1} alignItems="center">
					{headerRight}
					<IconButton onClick={onClose}>
						<Close />
					</IconButton>
				</Stack>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ p: 2.5 }}>
				{loading ? (
					<Grid container spacing={2}>
						<Grid item xs={12} md={5}>
							<Card elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
								<Skeleton variant="rounded" width="100%" height={220} />
								<Stack direction="row" spacing={1} mt={1}>
									<Skeleton variant="rounded" width={96} height={32} />
									<Skeleton variant="rounded" width={96} height={32} />
								</Stack>
							</Card>
						</Grid>
						<Grid item xs={12} md={7}>
							<Card elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
								<Skeleton width="60%" />
								<Skeleton width="40%" />
								<Divider sx={{ my: 2 }} />
								<Skeleton height={28} />
								<Skeleton height={28} />
								<Skeleton height={28} />
							</Card>
						</Grid>
					</Grid>
				) : ui ? (
					<Grid container spacing={2}>
						{/* Coluna Foto */}
						<Grid item xs={12} md={5}>
							<Card
								elevation={0}
								sx={{
									p: 2,
									borderRadius: 3,
									border: "1px dashed",
									borderColor: "divider",
									transition: "border-color .2s ease",
									"&:hover": { borderColor: "text.disabled" },
								}}
							>
								<Box
									sx={{
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 2,
										width: "100%",
										height: 220,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										overflow: "hidden",
										bgcolor: "background.default",
									}}
								>
									{photoError || !ui.photoUrl ? (
										<Avatar sx={{ width: 160, height: 160, bgcolor: "grey.300" }}>FOTO</Avatar>
									) : (
										<Box
											component="img"
											src={ui.photoUrl}
											alt="Foto Biometria"
											onError={() => setPhotoError(true)}
											sx={{ width: "100%", height: "100%", objectFit: "cover" }}
										/>
									)}
								</Box>

								<Stack direction="row" spacing={1} mt={1} justifyContent="space-between" alignItems="center">
									<Typography variant="caption" color="text.secondary">
										{ui.captureDate ? `Capturada em ${formatDateTime(ui.captureDate)}` : `Solicitada em ${formatDateTime(ui.requestDate)}`}
									</Typography>

									<Stack direction="row" spacing={1}>
										<Tooltip title={canDownload ? "Baixar imagem" : "Sem imagem"}>
											<span>
												<IconButton onClick={handleDownload} disabled={!canDownload} size="small">
													<DownloadIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
										{ui.shortUrl && (
											<Tooltip title="Abrir validação">
												<IconButton size="small" component={Link} href={ui.shortUrl} target="_blank" rel="noopener">
													<LinkIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
									</Stack>
								</Stack>
							</Card>
						</Grid>

						{/* Coluna Dados */}
						<Grid item xs={12} md={7}>
							<Card elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
								<CardContent sx={{ p: 2 }}>
									{/* Linha 1: metadados compactos */}
									<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
										<Chip size="small" variant="outlined" label={`Gateway: ${ui.gateway ?? "—"}`} />
										{ui.id && (
											<Chip
												size="small"
												variant="outlined"
												label={`ID: ${ui.id}`}
												icon={<ContentCopyIcon fontSize="small" />}
												onClick={() => navigator.clipboard.writeText(ui.id!)}
												sx={{ cursor: "pointer" }}
											/>
										)}
										{ui.analysisId && (
											<Chip
												size="small"
												variant="outlined"
												label={`Análise: ${ui.analysisId}`}
												icon={<ContentCopyIcon fontSize="small" />}
												onClick={() => navigator.clipboard.writeText(ui.analysisId!)}
												sx={{ cursor: "pointer" }}
											/>
										)}
									</Stack>

									{/* Mensagem “limpa” (sem prefixo comercial) */}
									{ui.message && (
										<>
											<Divider sx={{ my: 2 }} />
											<Typography variant="body2" color="text.secondary">
												{ui.message}
											</Typography>
										</>
									)}

									<Divider sx={{ my: 2 }} />

									{/* Grid de informações principais */}
									<Grid container spacing={1.5}>
										{ui.requestDate && (
											<Grid item xs={12} sm={6}>
												<InfoItemWithCopy label="Requisição" value={formatDateTime(ui.requestDate)} icon={<CalendarToday />} />
											</Grid>
										)}
										{ui.captureDate && (
											<Grid item xs={12} sm={6}>
												<InfoItemWithCopy label="Captura" value={formatDateTime(ui.captureDate)} icon={<CameraAltOutlined />} />
											</Grid>
										)}
										{ui.phone && (
											<Grid item xs={12} sm={6}>
												<InfoItemWithCopy label="Telefone" value={maskPhone(ui.phone)} icon={<Person2Outlined />} />
											</Grid>
										)}
										{ui.email && (
											<Grid item xs={12} sm={6}>
												<InfoItemWithCopy label="E-mail" value={ui.email} icon={<Person2Outlined />} />
											</Grid>
										)}
									</Grid>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				) : (
					<Box display="flex" alignItems="center" justifyContent="center" minHeight={220}>
						<Typography variant="body2" color="text.secondary">
							Nenhum dado encontrado.
						</Typography>
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
}
